import { LinkProps } from 'next/link';
import { ComponentType, CSSProperties, PropsWithChildren, ReactNode, useRef } from 'react';

import { AccountBadge } from './components/account-badge/account-badge';
import { CurrencySwitcher } from './components/currency-switcher/currency-switcher';
import { LanguageSwitcher } from './components/language-switcher/language-switcher';
import { Link } from './components/link/link';
import { Links } from './components/links/links';
import { Locations } from './components/locations/locations';
import { Logo } from './components/logo/logo';
import { MobileBackButton } from './components/mobile-back-button/mobile-back-button';
import { Settings } from './components/settings/settings';
import { SignUpLink } from './components/sign-up-link/sign-up-link';
import { useIsStuck } from './hooks/use-is-stuck';
import { TopNavigationProvider, type TopNavigationProviderProps } from './top-navigation.context';
import styles from './top-navigation.module.css';

type TopNavigationComposition = {
  AccountBadge: typeof AccountBadge;
  CurrencySwitcher: typeof CurrencySwitcher;
  LanguageSwitcher: typeof LanguageSwitcher;
  Logo: typeof Logo;
  Link: typeof Link;
  Links: typeof Links;
  Locations: typeof Locations;
  MobileBackButton: typeof MobileBackButton;
  Settings: typeof Settings;
  SignUpLink: typeof SignUpLink;
};

export type TopNavigationProps = PropsWithChildren<
  {
    id?: string;
    variant?: 'transparent' | 'filled' | undefined;
    position?: 'fixed' | 'sticky' | 'static' | undefined;
    style?: CSSProperties & {
      '--top-navigation-background-color'?: string;
      '--top-navigation-color'?: string;
    };
    linkFactory?: (props: LinkProps) => ReactNode;
  } & TopNavigationProviderProps
>;
export const TopNavigation = (({
  id,
  children,
  variant = 'filled',
  position = 'static',
  content,
  linkFactory,
  ...headerProps
}: TopNavigationProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isStuck = useIsStuck(
    {
      isSticky: !!position,
    },
    ref,
  );

  return (
    <TopNavigationProvider content={content} linkFactory={linkFactory}>
      <div className={styles.sentinel} ref={ref} />
      <header
        id={id}
        {...headerProps}
        className={styles.wrapper}
        data-variant={variant}
        data-position={position}
        data-stuck={isStuck}
      >
        {children}
      </header>
    </TopNavigationProvider>
  );
}) as ComponentType<TopNavigationProps> & TopNavigationComposition;
TopNavigation.AccountBadge = AccountBadge;
TopNavigation.CurrencySwitcher = CurrencySwitcher;
TopNavigation.LanguageSwitcher = LanguageSwitcher;
TopNavigation.Logo = Logo;
TopNavigation.Link = Link;
TopNavigation.Links = Links;
TopNavigation.Locations = Locations;
TopNavigation.MobileBackButton = MobileBackButton;
TopNavigation.Settings = Settings;
TopNavigation.SignUpLink = SignUpLink;
